import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import SidebarMenu from "../AdminPanel/SidebarMenu";
import TopBar from "../AdminPanel/TopBar";
import DailyBookDetail from "./DailyBook/DailyBookDetail";
import AdjustmentForm from "./DailyBook/AdjustmentForm";

const BLUE = "#1565C0";
const BLUE_DARK = "#0D47A1";
const BORDER = "#e2e8f0";
const TEXT = "#0f172a";
const MUTED = "#64748b";
const PAGE_BG = "#f5f6fa";

const DailyBook = () => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [asientos, setAsientos] = useState([]);
  const [opciones, setOpciones] = useState({ estados: [] });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [adjustOpen, setAdjustOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    search: "",
    fecha_desde: "",
    fecha_hasta: "",
    estado: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const pageSize = 20;

  const money = (value) => {
    const n = Number(value || 0);
    return n.toLocaleString("es-EC", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const showMessage = (message, type = "success") => {
    setSnackbar({
      open: true,
      type,
      message,
    });
  };

  const buildQuery = () => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("page_size", pageSize);

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params.set(key, value);
      }
    });

    return params.toString();
  };

  const cargarOpciones = async () => {
    try {
      const res = await fetch("/cuentas/libro-diario/opciones/", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.ok) {
        setOpciones({
          estados: data.estados || [],
        });
      }
    } catch {
      showMessage("No se pudieron cargar las opciones del libro diario.", "error");
    }
  };

  const cargarLibro = async () => {
    setLoading(true);

    try {
      const res = await fetch(`/cuentas/libro-diario/listar/?${buildQuery()}`, {
        credentials: "include",
      });

      const data = await res.json();

      if (!data.ok) {
        throw new Error(data.error || "No se pudo cargar el libro diario.");
      }

      setAsientos(data.results || []);
      setCount(data.count || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      showMessage(error.message || "No se pudo cargar el libro diario.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarOpciones();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setExpanded({});
      cargarLibro();
    }, 350);

    return () => clearTimeout(timer);
  }, [page, filters]);

  const totalesPagina = useMemo(() => {
    return asientos.reduce(
      (acc, asiento) => {
        acc.debe += Number(asiento.total_debe || 0);
        acc.haber += Number(asiento.total_haber || 0);
        return acc;
      },
      { debe: 0, haber: 0 }
    );
  }, [asientos]);

  const updateFilter = (field, value) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleDetalle = (id) => {
    setExpanded((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const reloadAfterAdjust = () => {
    setAdjustOpen(false);
    setExpanded({});
    setPage(1);
    cargarLibro();
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", {
        method: "POST",
        credentials: "include",
      });
    } catch {}

    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: PAGE_BG }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              onClick={() => setAdjustOpen(true)}
              sx={{
                textTransform: "none",
                bgcolor: BLUE,
                "&:hover": { bgcolor: BLUE_DARK },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "none",
              }}
            >
              Nuevo ajuste
            </Button>
          </Box>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              boxShadow: "none",
              border: `1px solid ${BORDER}`,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
              mb={2}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: TEXT }}>
                  Libro Diario
                </Typography>
                <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.4 }}>
                  Registro de asientos contables generados por documentos, pagos y ajustes
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: 13, color: TEXT, fontWeight: 700 }}>
                  Registros: {count}
                </Typography>

                <Typography sx={{ fontSize: 13, color: TEXT, fontWeight: 700 }}>
                  Debe: ${money(totalesPagina.debe)}
                </Typography>

                <Typography sx={{ fontSize: 13, color: TEXT, fontWeight: 700 }}>
                  Haber: ${money(totalesPagina.haber)}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar"
                  value={filters.search}
                  onChange={(e) => updateFilter("search", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={2.2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Desde"
                  type="date"
                  value={filters.fecha_desde}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => updateFilter("fecha_desde", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={2.2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Hasta"
                  type="date"
                  value={filters.fecha_hasta}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => updateFilter("fecha_hasta", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={2.6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estado</InputLabel>
                  <Select
                    label="Estado"
                    value={filters.estado}
                    onChange={(e) => updateFilter("estado", e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {opciones.estados.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {loading && (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <CircularProgress size={28} sx={{ color: BLUE }} />
              </Box>
            )}

            {!loading && asientos.length === 0 && (
              <Box
                sx={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  bgcolor: "#fff",
                }}
              >
                <Typography sx={{ color: MUTED, fontWeight: 700 }}>
                  No existen asientos registrados.
                </Typography>
              </Box>
            )}

            {!loading &&
              asientos.map((asiento) => (
                <Box
                  key={asiento.id}
                  sx={{
                    border: `1px solid ${BORDER}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    mb: 1.3,
                    bgcolor: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "120px 170px 1fr 120px 120px 100px",
                      },
                      gap: 1.5,
                      alignItems: "center",
                      px: 2,
                      py: 1.4,
                      borderBottom: expanded[asiento.id] ? `1px solid ${BORDER}` : "none",
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 700 }}>
                        Fecha
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: TEXT, fontWeight: 700 }}>
                        {asiento.fecha}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 700 }}>
                        Asiento
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: TEXT, fontWeight: 700 }}>
                        {asiento.numero}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 700 }}>
                        Descripción
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: TEXT, fontWeight: 700 }}>
                        {asiento.descripcion || "-"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>
                        {asiento.subtitulo || `${asiento.tipo_label} · ${asiento.origen_label} · ${asiento.estado_label}`}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                      <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 700 }}>
                        Debe
                      </Typography>
                      <Typography sx={{ fontSize: 15, color: TEXT, fontWeight: 800 }}>
                        ${money(asiento.total_debe)}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                      <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 700 }}>
                        Haber
                      </Typography>
                      <Typography sx={{ fontSize: 15, color: TEXT, fontWeight: 800 }}>
                        ${money(asiento.total_haber)}
                      </Typography>
                    </Box>

                    <Button
                      onClick={() => toggleDetalle(asiento.id)}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: BLUE,
                        color: BLUE,
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 2,
                      }}
                    >
                      {expanded[asiento.id] ? "Ocultar" : "Ver"}
                    </Button>
                  </Box>

                  <Collapse in={!!expanded[asiento.id]} timeout="auto" unmountOnExit>
                    <DailyBookDetail asiento={asiento} money={money} />
                  </Collapse>
                </Box>
              ))}

            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
                Página {page} de {totalPages}
              </Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  variant="outlined"
                  sx={{
                    borderColor: BORDER,
                    color: TEXT,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Anterior
                </Button>

                <Button
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                  variant="outlined"
                  sx={{
                    borderColor: BORDER,
                    color: TEXT,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      <AdjustmentForm
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onSaved={reloadAfterAdjust}
        showMessage={showMessage}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DailyBook;